import { Card } from "react-bootstrap";
import { Chart as Windrose } from "@eunchurn/react-windrose";
import { useMemo } from "react";

type Angle =
  | "N" | "NNE" | "NE" | "ENE"
  | "E" | "ESE" | "SE" | "SSE"
  | "S" | "SSW" | "SW" | "WSW"
  | "W" | "WNW" | "NW" | "NNW";

const compassDirections: Angle[] = [
  "N", "NNE", "NE", "ENE", "E", "ESE", "SE", "SSE",
  "S", "SSW", "SW", "WSW", "W", "WNW", "NW", "NNW"
];

interface Label {
  label: string;
  column_index: number;
  multiplier?: number;
  offset?: number;
}

interface WindroseChartCardProps {
  config: {
    device_id: number;
    chart_labels: Label[];
  };
  data: {
    timestamp: string;
    values: Record<string, number | null>
  }[];
  rangeLabel: string;
}

function degreesToCompass(angleDeg: number): Angle {
  const index = Math.round((angleDeg % 360) / 22.5) % 16;
  return compassDirections[index];
}

export function WindroseChartCard({ config, data, rangeLabel }: WindroseChartCardProps) {
  const chartData = useMemo(() => {
    if (config.chart_labels.length < 2) return [];

    const speedIndex = config.chart_labels[0].column_index.toString();
    const directionIndex = config.chart_labels[1].column_index.toString();

    const grouped: Record<Angle, number[]> = {} as Record<Angle, number[]>;
    compassDirections.forEach((dir) => {
      grouped[dir] = [];
    });

    for (const row of data) {
      const rawSpeed = row.values?.[speedIndex];
      const rawDegrees = row.values?.[directionIndex];

      const speedLabel = config.chart_labels[0];
      // const directionLabel = config.chart_labels[1];

      const multiplier = speedLabel.multiplier ?? 1;
      const offset = speedLabel.offset ?? 0;

      const speed = rawSpeed != null ? rawSpeed * multiplier + offset : null;
      const degrees = rawDegrees;

      // console.log(`🌬 rawSpeed: ${rawSpeed}, rawDegrees: ${rawDegrees}`);
      // console.log(`🌀 Windrose speed: ${speed}, multiplier: ${multiplier}, offset: ${offset}`);
      

      if (speed != null && degrees != null && !isNaN(degrees)) {
        const angle = degreesToCompass(degrees);
        grouped[angle].push(speed);
      }
    }

    const bins = ["<.5", "<2", "2–4", "4–6", "6–8", "8–10", ">10"] as const;

    const result = compassDirections.map((dir) => {
      const speeds = grouped[dir];
      const total = speeds.length || 1;

      const binValues: Record<typeof bins[number], number> = {
        "<.5": 0,
        "<2": 0,
        "2–4": 0,
        "4–6": 0,
        "6–8": 0,
        "8–10": 0,
        ">10": 0,
      };

      // console.log(`📊 Direction ${dir}`, { speeds, binValues, total });

      for (const s of speeds) {
        if (s < 0.5) binValues["<.5"]++;
        else if (s < 2) binValues["<2"]++;
        else if (s < 4) binValues["2–4"]++;
        else if (s < 6) binValues["4–6"]++;
        else if (s < 8) binValues["6–8"]++;
        else if (s < 10) binValues["8–10"]++;
        else binValues[">10"]++;
      }

      // Normalize and round
      Object.keys(binValues).forEach((k) => {
        const key = k as keyof typeof binValues;
        binValues[key] = +(binValues[key] / total).toFixed(3);
      });

      return {
        angle: dir,
        ...binValues,
      };
    });

    // ❗ Type assertion to bypass strict typing from library
    return result as any;
  }, [data, config]);

  return (
    <Card className="h-auto w-auto" style={{ overflow: "hidden" }}>
      <Card.Body className="d-flex flex-column h-100 p-3">
        {/* <div className="text-center text-muted small mb-2">{range.toUpperCase()}</div> */}
        <div className="text-center text-muted small mb-2">{rangeLabel}</div>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            paddingLeft: "1rem",
            paddingRight: "1rem",
            overflowX: "auto", // allow scrolling if too tight
          }}
        >
          <div style={{ }}> {/* Give it room for the chart + legend */}
            <Windrose
              key={rangeLabel + chartData.length} 
              chartData={chartData}
              columns={["angle", "<.5", "<2", "2–4", "4–6", "6–8", "8-10", ">10"]}
              responsive
              legendGap={0}
            />
          </div>
        </div>
      </Card.Body>
    </Card>
  );
}

export default WindroseChartCard;
